<template>
  <div>
    <div
      class="page-community"
      :class="{ dark: darkMode }"
    >
      <div
        v-if="page.video && !(isTabletPortrait || isLargeMobile || isMobile)"
        class="video-player"
      >
        <video
          :poster="videoPosterImage"
          :src="page.video.url"
          autoplay
          muted
          loop
        />
      </div>
      <HeaderImage
        v-else
        :image="page.image"
        :image-mobile="page.imageMobile"
        :alt="page.title"
        class="background-image"
      />
      <div class="layer-container">
        <div class="layer">
          <p class="page-type">
            communauté
            <span v-if="page.type != 'libre'">{{ page.type }}</span>
          </p>
          <h1>{{ page.title }}</h1>
          <div class="sub-line">
            <div
              v-if="page.follow"
              class="button sub"
              @click="tryUnfollow"
            >
              Abonné
              <img
                src="~assets/icons/check.svg"
                alt="check"
                class="check"
              />
            </div>
            <div
              v-else
              class="button no-sub"
              @click="tryFollow"
            >
              S'abonner
            </div>
            <img
              v-if="page.pegi"
              :src="`/icons/PEGI_${page.pegi}.svg`"
              :alt="`pegi ${page.pegi}`"
            />
          </div>
          <p class="description">{{ page.description }}</p>
          <div class="detail-top">
            <template v-if="page.showReview">
              <div
                v-if="page.score"
                class="score"
              >
                <p>Note</p>
                <div class="line score-line">
                  <ScoreResult
                    :score="page.score"
                    class="score-item"
                  />
                  <NuxtLink
                    :to="`/communautes/${page.slug}/avis-joueurs`"
                    class="avis"
                  >
                    {{ page.opinionsCount }} avis
                  </NuxtLink>
                </div>
              </div>
              <div
                v-if="page.percentReco || page.percentReco === 0"
                class="reco"
              >
                <p>Recommandé par</p>
                <p class="numbers">
                  <span class="big">{{ page.percentReco }}%</span> des joueurs
                </p>
              </div>
            </template>
          </div>
          <div
            v-if="page.details"
            class="detail-bottom"
          >
            <div
              v-for="detail in page.details"
              :key="detail.label"
              class="detail-item"
            >
              <p class="label">{{ detail.label }} :</p>
              <template v-if="'links' in detail && detail.links.length">
                <p class="value">
                  <template
                    v-for="(link, index) in detail.links"
                    :key="link.slug"
                  >
                    <NuxtLink :to="`/communautes/genre/${link.slug}`">{{
                      link.name
                    }}</NuxtLink>
                    {{ index !== detail.links.length - 1 ? ', ' : '' }}
                  </template>
                </p>
              </template>
              <p
                v-else-if="'value' in detail"
                class="value"
              >
                {{ detail.value }}
              </p>
            </div>
          </div>
          <ButtonPoint
            :points="page.contribPoints"
            class="button-point-item"
            label="Ajouter un contenu"
            @click="openContribution"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { checkLoginModal } from '~/modals/checkLogin'
import {
  contributionModal,
  setContributionModalTab,
} from '~/modals/contribution'
import type { CommunityPageInfo } from '~/types'

const props = withDefaults(
  defineProps<{
    page: CommunityPageInfo
    darkMode?: boolean
    subscribed?: boolean
  }>(),
  {
    darkMode: false,
    subscribed: false,
  },
)
const img = useImage()

const videoPosterImage = computed(() => {
  if (!props.page.image) return

  return img(props.page.image, { width: 1280, format: 'auto', quality: 80 })
})

const communitiesStore = useCommunitiesStore()
const { isMobile, isLargeMobile, isTabletPortrait } = useCustomMediaQuery()

const { authenticated } = useAuthenticated()

const tryFollow = async () => {
  if (!authenticated.value) {
    return checkLoginModal.open()
  }

  const followed = await communitiesStore.followCommunity(props.page.slug)
  if (!followed) return

  track('abonnement', {
    nomcommunaute: props.page.title,
  })

  // a subscription that wins a badge shows its reward, as a contribution or a challenge does (the modal also marks
  // the badge notification read)
  if (followed.badges_reward?.length) {
    const { openLoginRewardModal } = await import('~/modals/login')
    await openLoginRewardModal({
      title: `Bravo ${useAuth().data.value?.firstname ?? ''}`.trim(),
      messages: [
        `Vous êtes abonné à la communauté <strong>${escapeHtmlAttribute(props.page.title)}</strong> et vous gagnez un badge !`,
      ],
      badges: followed.badges_reward,
      points: followed.points_total,
      actions: [
        {
          type: 'route',
          route: '/mon-profil',
          label: 'Voir mon profil',
          outline: true,
        },
        {
          type: 'route',
          route: '/avantages',
          label: 'Découvrir mes avantages',
        },
      ],
    })
  }
}

const tryUnfollow = () => {
  if (authenticated.value) {
    return communitiesStore.unfollowCommunity(props.page.slug)
  }
}

const openContribution = () => {
  track('cta', {
    cta_name: 'Ajouter un contenu',
  })
  if (!authenticated.value) {
    return checkLoginModal.open()
  }

  setContributionModalTab('source')
  contributionModal.open()
}
</script>
<style lang="scss" scoped>
.page-community {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  // margin-top: 3rem;
  background-color: #282828;

  @media (min-width: 992px) {
    height: 90vh;
    min-height: 68rem;
    max-height: 73rem;
    margin-top: 0;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    width: 0;
    height: 0;
    border-width: 0 0 3rem 100vw;
    border-style: solid;
    border-color: transparent transparent $white;

    @media (min-width: 992px) {
      border-width: 0 0 5rem 100vw;
    }
  }

  .background-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    object-position: top;

    @media (max-width: 991px) {
      max-height: 465px;
    }

    @media (max-width: 430px) {
      max-height: 465px;
    }
  }

  .video-player {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    @media (max-width: 991px) {
      // max-height: 90vh;
      max-height: 465px;
    }

    video {
      width: 100%;
      height: 100%;
      object-position: top;
      object-fit: cover;
    }
  }

  &.dark {
    &::after {
      border-color: transparent transparent $darkBackground;
    }
  }

  .layer-container {
    @media (max-width: 991px) {
      width: 100%;
    }

    @media screen and (min-width: 2000px) {
      position: absolute;
      top: 0;
      left: 0;
      width: calc(50vw - 100rem + 56.6rem);
      height: 100%;
      background: rgba(#000, 0.6);
    }
  }

  .layer {
    position: relative;
    width: 100%;
    height: 100%;
    margin-top: 230px;
    padding: 3.5rem 2rem 7rem;
    background: rgba(#000, 0.6);

    @media (min-width: 992px) {
      position: absolute;
      top: 0;
      left: 0;
      max-width: 56.6rem;
      margin-top: 0;
      padding: 5rem 8rem;
    }

    @media screen and (max-width: 700px) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }

    @media screen and (min-width: 2000px) {
      right: 0;
      left: auto;
      background: transparent;
    }
  }

  .page-type {
    color: $white;
    font-family: 'SST Condensed';
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.3rem;
    letter-spacing: 0.1rem;
    text-transform: uppercase;
    opacity: 0.5;
  }

  h1 {
    margin-top: 1.8rem;
    color: $white;
    font-family: SST;
    font-size: 3.2rem;
    font-weight: 400;
    line-height: 4.1rem;
    letter-spacing: 0;
  }

  .description {
    max-width: 37.4rem;
    margin-top: 5rem;
    color: $white;
    font-size: 1.2rem;
    line-height: 1.6rem;

    @media (min-width: 992px) {
      margin-top: 4rem;
    }
  }

  .sub-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 3.3rem;

    @media screen and (max-width: 700px) {
      width: 100%;
    }

    .button {
      height: 4.6rem;
      padding: 0 2rem;
      color: $white;
      line-height: 4.6rem;

      &.no-sub {
        background: $backgroundBlue;

        &:hover {
          background: #0089d8;
        }
      }

      &.sub {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #999;

        img {
          width: 1.1rem;
          height: 9.7rem;
          margin-left: 1rem;
        }
      }
    }

    img {
      width: 3.656rem;
      height: 4.6rem;
    }
  }

  .detail-top {
    display: flex;
    justify-content: space-between;
    margin-top: 1.6rem;
    color: $white;
    border-bottom: 1px solid rgba(#fff, 0.2);

    @media screen and (max-width: 700px) {
      width: 100%;
    }

    p {
      margin-bottom: 1rem;
      font-size: 1.2rem;
      line-height: 1.6rem;
    }

    .score-line {
      @media screen and (max-width: 700px) {
        flex-wrap: wrap;
      }
    }

    .score-item {
      margin-right: 2rem;

      @media screen and (max-width: 700px) {
        flex-shrink: 0;
      }
    }

    .avis {
      color: $white;
      font-size: 1.2rem;
      font-weight: 300;
      line-height: 2.2rem;
      text-decoration: none;
      border-bottom: 1px solid rgba(#fff, 0.6);
      opacity: 0.6;
    }

    .reco {
      flex-basis: 40%;

      .numbers {
        font-family: 'SST Condensed';
        font-size: 1.4rem;
        font-weight: bold;
        line-height: 1.8rem;

        .big {
          font-size: 2.4rem;
        }
      }
    }
  }

  .button-point-item {
    margin-top: 4rem;

    @media screen and (max-width: 700px) {
      position: relative;
      bottom: auto;
      left: auto;
      margin-top: 5rem;
    }
  }

  .detail-bottom {
    display: flex;
    flex-wrap: wrap;
    min-height: 9rem;

    @media screen and (max-width: 700px) {
      width: 100%;
    }

    .detail-item {
      flex-basis: 60%;
      margin-top: 1.4rem;

      &:nth-child(2n) {
        flex-basis: 40%;
      }

      p {
        font-size: 1.2rem;
        line-height: 1.6rem;
      }

      .label {
        color: $white;
      }

      .value {
        color: #4294d0;
      }

      a {
        color: #4294d0;
        text-decoration: none;
      }
    }
  }
}
</style>
